<template>
    <div class="row">
        <div class="col-12">
            <div class="card mb-1 p-1">
                <DentitionPlan 
                    :dentition="dentition" 
                    :dentitionType="dentitionType" 
                    :billedDentition="billedDentition" 
                />
            </div>
        </div>
        <div class="col-12">
            <div class="card mb-1 p-1">
                <TreatmentPlanCard 
                    :note="treatmentPlan"
                    :salesOrderId="salesOrderId"
                    :generalId="generalId"
                    :dentition="dentition"
                    :dentitionType="dentitionType"
                    :clientId="bill.contact_profile_id"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue';
import TreatmentPlanCard from '@/components/molecule/company/dental-os/service/middle-header/TreatmentPlan.vue';
import DentitionPlan from '@/components/molecule/company/dental-os/service/middle-header/DentitionPlan.vue';
import handleInventory from "@/services/modules/inventory";
import { computed } from "vue";
import { useRoute } from "vue-router";
import handlePurchase from "@/services/modules/purchase";
import { cloneDeep } from 'lodash';
import { pluck } from '@/services/utils/global';

const { getProduct } = handleInventory();
const showError = inject('showError');
const route = useRoute();
const { fetchSalesOrderById } = handlePurchase();
const props = defineProps({
    resourceDescriptions: {
        type: Array,
        default: []
    },
    treatmentPlan: {
        type: Object,
        default: () => {}
    },
    ipdRegister: {
        type: Object || null,
        default: () => {}
    },
    bill: {
      type: Object || null,
      default: () => {}
    },
    generalId: {
        type: Number || null,
        default: null
    },
    defaultProductId: {
        type: Number || null,
        default: null
    },
    dentition: {
        type: Object || null,
        default: () => {}
    },
    dentitionType: {
        type: String || null,
        default: null,
    },
});

const salesOrder = ref(null);
const companyId = computed(() => route.params.companyId);
const salesOrderId = computed(() => props.ipdRegister ? props.ipdRegister.sales_order_id : null);
const billedDentition = ref({});

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&show_service_task=1`
    return query;
}

const setInitialData = () => {
    getSalesOrder();
}

const getSalesOrder = async () => {
    
    if(!salesOrderId.value ) return;

    await fetchSalesOrderById(salesOrderId.value, getQuery()).then(res => {
        if(res.data) {
            salesOrder.value = res.data;
            setSalesOrderProducts();
        }
    })
}

const setSalesOrderProducts = () => {
    const existingIds = props.treatmentPlan.selectedNotes.map(item => item.product_id);
    
    for(const item of salesOrder.value.general) {

        if (existingIds.includes(item.product_id)) {
            continue;
        }
        
        const product = cloneDeep(item.product);
        product.quantity = item.quantity;
        product.rate = item.rate;
        product.vat = item.vat;
        product.discount = item.discount;
        product.discount_amount = item.discount_amount;
        product.discount_percent = item.discount_percent;
        product.vat_amount = item.vat_amount;
        product.has_prescription = item.prescription_count > 0 ? true : false;
        product.dentition = item.dentition_pertains ? pluck(item.dentition_pertains, 'tooth_number') : [];
        product.dentition_pertains = item.dentition_pertains || [];
        product.amount = item.amount;
        product.total_amount = item.total_amount;
        product.status = item.status;
        product.schedule_date = item.schedule_date;
        product.items = item.items || [];
        product.is_done = item.is_done || false;
        product.invoice_qty = item.invoice_qty;

        if(item.contact_profile_id) {
            product.service_resource = {
                id: item.contact_profile_id,
                name: item.contact_profile_name
            }
        }

        product.is_default_disabled = route.query.general_id ? true : false;

        props.treatmentPlan.selectedNotes.push(setProductData(product, item.id));

        item.dentition_pertains.filter(dentition => dentition.is_billed)
            .forEach(dentition => {
                if( ! billedDentition.value.hasOwnProperty(dentition.tooth_number) ) {
                    billedDentition.value[dentition.tooth_number] = [];
                }

                billedDentition.value[dentition.tooth_number].push(product);
            });
    }
}

const setDefaultProduct = async() => {
    
    if(!props.defaultProductId) {
        return;
    }

    const productResp = await getProduct({
        id: props.defaultProductId,
        company_id: companyId.value
    })

    if (!productResp.data) {
        showError("Something went wrong please try again");
        return;
    }

    const checkExist = props.treatmentPlan.selectedNotes.some(item => item.product_id === productResp.data.id);
    if(checkExist) {
        return;
    }

    let selected = productResp.data;
    selected.rate = selected.description.sales_price;
    selected.vat = selected.description.vat_rate;
    selected.quantity = 1;
    selected.is_default_disabled = true;
    selected.service_resource = props.bill.service_resource || null
    selected.service_resource.id = props.bill.service_resource.contact_profile_id || null
    selected.schedule_date = props.bill.start_date_time || null

    props.treatmentPlan.selectedNotes.unshift(setProductData(selected));
}

const setProductData = (product, id = null) => {
    const formattedProduct = {
        id: id,
        product_id: product.id,
        name: product.name,
        description: product.description && product.description.unit ? product.description.unit.description : '',
        service_resource: product.service_resource || null,
        quantity: product.quantity,
        rate: product.rate,
        discount: product.discount || 0,
        vat: product.vat,
        sub_total: product.amount || 0,
        discount_amount: product.discount_amount || 0,
        discount_percent: product.discount_percent || 0,
        vat_amount: product.vat_amount || 0,
        total: product.total_amount || 0,
        isEdit: false,
        product: product,
        has_prescription: product.has_prescription || false,
        is_default_disabled: product.is_default_disabled || false,
        dentition: product.dentition || [],
        status: product.status,
        schedule_date: product.schedule_date,
        dentition_pertains: product.dentition_pertains || [],
        items: product.items || [],
        is_done: product.is_done || false,
        invoice_qty: product.invoice_qty || 0
    }
    return formattedProduct;
}

onMounted(async () => {
    if(! salesOrderId.value) {
        await setDefaultProduct();
    }

    setInitialData();
});

</script>
